import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../firebase";
import { collection, getDocs, query, where, Timestamp, updateDoc, doc } from "firebase/firestore";
import { Box, Typography, Grid, Paper, CircularProgress, Stack, Chip, Divider } from "@mui/material";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow, Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import Swal from 'sweetalert2';
import { TextField, Button } from "@mui/material";
import TodayIcon from "@mui/icons-material/Today";
import DateRangeIcon from "@mui/icons-material/DateRange";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function Dashboard() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 4000,
    timerProgressBar: true,
  });
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dueCustomers, setDueCustomers] = useState([]);
  const [vehicleReminders, setVehicleReminders] = useState([]);
  const [weightAlerts, setWeightAlerts] = useState([]);
  const [openAlertPopup, setOpenAlertPopup] = useState(false);
  const [birthdayAlerts, setBirthdayAlerts] = useState([]);
  const [whatsappStats, setWhatsappStats] = useState({
    total: 0,
    sent: 0,
    remaining: 0
  });
  const [stats, setStats] = useState({
    sale: 0,
    purchase: 0,
    todaysCollection: 0,
    pendingCollection: 0,
    pendingPayment: 0, // 🔥 Added for (Total Sale - Trip Payments)
    expense: 0,
    custCount: 0,
    dealerCount: 0,
    totalBrass: 0
  });
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    return d.toISOString().split("T")[0];
  });

  const [toDate, setToDate] = useState(() => {
    const d = new Date();
    return d.toISOString().split("T")[0];
  });

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(user => setUserData(user));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!userData) return;

    const loadData = async () => {
      try {
        const start = new Date(fromDate);
        start.setHours(0, 0, 0, 0);

        const end = new Date(toDate);
        end.setHours(23, 59, 59, 999);

        const startTS = Timestamp.fromDate(start);
        const endTS = Timestamp.fromDate(end);

        const [
          tripsSnap,
          purchaseSnap,
          custPaySnap,
          dealerPaySnap,
          custSnap,
          dealerSnap
        ] = await Promise.all([
          getDocs(query(
            collection(db, "trips"),
            where("grossDate", ">=", startTS),
            where("grossDate", "<=", endTS)
          )),
          getDocs(query(
            collection(db, "purchases")
          )),
          getDocs(query(
            collection(db, "customer_history"),
            where("date", ">=", startTS),
            where("date", "<=", endTS)
          )),
          getDocs(query(
            collection(db, "dealerPayments"),
            where("date", ">=", startTS),
            where("date", "<=", endTS)
          )),
          getDocs(collection(db, "customers")),
          getDocs(collection(db, "dealers"))
        ]);

        let totalSale = 0;
        let tripCount = 0;
        let totalPurchase = 0;
        let totalTodaysCollection = 0;   // Trip-tied collections (Payments1)
        let totalPendingCollection = 0;  // Non-trip collections (Payments)
        let totalExpense = 0;
        let totalBrass = 0;

        // 1. TRIPS SALE
        tripsSnap.forEach(doc => {
          const data = doc.data();
          let tripTotal = 0;

          if (Array.isArray(data.products)) {
            tripTotal = data.products.reduce((sum, p) => {
              totalBrass += Number(p.qty || 0);
              if (p.amount) return sum + Number(p.amount);
              return sum + (Number(p.qty || 0) * Number(p.rate || 0));
            }, 0);
          } else {
            tripTotal =
              Number(data.totalAmount || 0) ||
              Number(data.amount || 0) ||
              0;
          }

          if (tripTotal > 0) {
            totalSale += tripTotal;
            tripCount++;
          }
        });

        // 2. PURCHASE
        purchaseSnap.forEach(doc => {
          const data = doc.data();
          let purchaseDate;
          if (data.purchasedate) {
            if (data.purchasedate.seconds) {
              purchaseDate = new Date(data.purchasedate.seconds * 1000);
            } else {
              purchaseDate = new Date(data.purchasedate);
            }
          } else {
            return;
          }

          if (purchaseDate < start || purchaseDate > end) return;

          let purchaseTotal = 0;

          if (Array.isArray(data.items)) {
            purchaseTotal = data.items.reduce((sum, p) => {
              if (p.total) return sum + Number(p.total);
              return sum + (Number(p.qty || 0) * Number(p.rate || 0));
            }, 0);
          } else {
            purchaseTotal =
              Number(data.grandtotal || 0) ||
              Number(data.total || 0) ||
              0;
          }

          totalPurchase += purchaseTotal;
        });

        // 3. CUSTOMER PAYMENTS PROCESSING
        custPaySnap.forEach(doc => {
          const data = doc.data();
          const amount = Number(data.amount || 0);

          if (data.tripId) {
            // payments1: linked directly to a trip invoice
            totalTodaysCollection += amount;
          } else {
            // payments: base/arrears processing
            totalPendingCollection += amount;
          }
        });

        // 4. DEALER PAYMENTS (EXPENSE)
        dealerPaySnap.forEach(doc => {
          const data = doc.data();
          totalExpense += Number(data.amount || 0);
        });

        // 🔥 5. CALCULATE PENDING PAYMENT: (Total Sale - Trip Payments Received)
        const totalPendingPayment = totalSale - totalTodaysCollection;

        setStats({
          sale: totalSale,
          tripCount: tripCount,
          purchase: totalPurchase,
          todaysCollection: totalTodaysCollection,
          pendingCollection: totalPendingCollection,
          pendingPayment: totalPendingPayment < 0 ? 0 : totalPendingPayment, // Safe fallback
          expense: totalExpense,
          custCount: custSnap.size,
          dealerCount: dealerSnap.size,
          totalBrass: totalBrass
        });

        setLoading(false);
      } catch (err) {
        console.error("Dashboard Load Error:", err);
        setLoading(false);
      }
    };

    loadData();
    loadCustomers();
    loadVehicleReminders();
    loadWeightAlerts();
  }, [userData, fromDate, toDate]);

  const loadBirthdayAlerts = async () => {
    try {
      const snap = await getDocs(collection(db, "staff"));
      const today = new Date();
      const currentYear = today.getFullYear();
      const next3Days = [];
      for (let i = 0; i <= 2; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        next3Days.push({
          day: d.getDate(),
          month: d.getMonth() + 1
        });
      }

      const birthdays = [];
      snap.forEach(doc => {
        const staff = doc.data();
        if (!staff.dob) return;
        const dob = new Date(staff.dob);
        const day = dob.getDate();
        const month = dob.getMonth() + 1;
        const match = next3Days.find(
          x => x.day === day && x.month === month
        );
        if (match) {
          birthdays.push({
            id: doc.id,
            name: staff.name,
            dob: staff.dob
          });
        }
      });

      birthdays.sort(
        (a, b) =>
          new Date(`${currentYear}-${a.dob.substring(5)}`) -
          new Date(`${currentYear}-${b.dob.substring(5)}`)
      );
      setBirthdayAlerts(birthdays);
    } catch (err) {
      console.log(err);
    }
  };

  const loadWhatsappStats = async () => {
    try {
      const countSnap = await getDocs(collection(db, "whatsappcount"));
      let totalMsg = 0;
      if (!countSnap.empty) {
        const firstDoc = countSnap.docs[0].data();
        totalMsg = Number(firstDoc.totalMsg || 0);
      }

      const historySnap = await getDocs(collection(db, "whatsapp_history"));
      let sentCount = 0;
      historySnap.forEach(doc => {
        const data = doc.data();
        if (data.status === "Sent") {
          sentCount++;
        }
      });

      const remaining = totalMsg - sentCount;
      setWhatsappStats({
        total: totalMsg,
        sent: sentCount,
        remaining: remaining
      });
    } catch (err) {
      console.error("WhatsApp Stats Error:", err);
    }
  };

  useEffect(() => {
    if (!userData) return;
    loadWhatsappStats();
    loadBirthdayAlerts();
  }, [userData]);

  const loadWeightAlerts = async () => {
    try {
      const q = query(
        collection(db, "weight_alerts"),
        where("status", "==", "Unread")
      );
      const snap = await getDocs(q);
      const alerts = snap.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));

      if (alerts.length > 0) {
        setWeightAlerts(alerts);
        setOpenAlertPopup(true);
      }
    } catch (err) {
      console.log("Weight Alert Fetch Error:", err);
    }
  };

  const markAllAsRead = async () => {
    try {
      const promises = weightAlerts.map(a =>
        updateDoc(doc(db, "weight_alerts", a.id), {
          status: "Read"
        })
      );
      await Promise.all(promises);
      setOpenAlertPopup(false);
      setWeightAlerts([]);
      Toast.fire({
        icon: "success",
        title: "All alerts marked as read"
      });
    } catch (err) {
      console.log("Mark Read Error:", err);
    }
  };

  const loadVehicleReminders = async () => {
    const snap = await getDocs(collection(db, "matoshrivehicles"));
    const vehicles = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const reminders = [];

    vehicles.forEach(v => {
      if (v.pucDate) {
        const pucDate = v.pucDate.seconds ? new Date(v.pucDate.seconds * 1000) : new Date(v.pucDate);
        pucDate.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((pucDate - today) / (1000 * 60 * 60 * 24));
        if (diffDays <= 30) {
          reminders.push({
            id: v.id + "_puc",
            vehicalNo: v.vehicalNo,
            type: "PUC",
            dueDate: pucDate,
            daysRemaining: diffDays
          });
        }
      }

      if (v.insuranceDate) {
        const insDate = v.insuranceDate.seconds ? new Date(v.insuranceDate.seconds * 1000) : new Date(v.insuranceDate);
        insDate.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((insDate - today) / (1000 * 60 * 60 * 24));
        if (diffDays <= 30) {
          reminders.push({
            id: v.id + "_ins",
            vehicalNo: v.vehicalNo,
            type: "Insurance",
            dueDate: insDate,
            daysRemaining: diffDays
          });
        }
      }
    });
    setVehicleReminders(reminders);
  };

  const loadCustomers = async () => {
    const snap = await getDocs(collection(db, "customers"));
    const allCustomers = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const due = allCustomers.filter(c => {
      if (!c.reminderDate) return false;
      let reminderDateObj;
      if (c.reminderDate.seconds) {
        reminderDateObj = new Date(c.reminderDate.seconds * 1000);
      } else {
        reminderDateObj = new Date(c.reminderDate);
      }
      reminderDateObj.setHours(0, 0, 0, 0);
      return reminderDateObj <= today;
    });
    setDueCustomers(due);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    if (date.seconds) return new Date(date.seconds * 1000).toLocaleDateString();
    return new Date(date).toLocaleDateString();
  };

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#f3f4f6' }}>
      <CircularProgress size={40} />
      <Typography sx={{ mt: 2, fontWeight: 600, color: '#64748b' }}>Calculating Today's Stats...</Typography>
    </Box>
  );

  const BOXES = [
    { title: "Today's Sale", value: stats.sale, color: "#3b82f6", link: "#", icon: "fa fa-file" },
    { title: "Today's Total Trips", value: stats.tripCount, color: "#e90ec5", link: "#", icon: "fa fa-truck" },
    { title: "Total Brass", value: stats.totalBrass, color: "#a24cf8", link: "#", icon: "fa fa-cubes" },
    { title: "Today's Purchase", value: stats.purchase, color: "#f59e0b", link: "#", icon: "fa fa-shopping-cart" },
    { title: "Today's Collection", value: stats.todaysCollection, color: "#10b981", link: "#", icon: "fa fa-money" },
    { title: "General Collection", value: stats.pendingCollection, color: "#21143e", link: "#", icon: "fa fa-folder-open-o" },
    { title: "Pending Payment", value: stats.pendingPayment, color: "#f43f5e", link: "#", icon: "fa fa-clock-o" },
    { title: "Today's Expense", value: stats.expense, color: "#651c1c", link: "#", icon: "fa fa-address-card" },
    { title: "WhatsApp Wallet", value: whatsappStats?.remaining, color: "#026402", link: "#", icon: "fa fa-whatsapp" },
  ];

  const QUICK_BOXES = [
    { title: "Trips", icon: "fa fa-list", link: "/add-trip", color: "#0ea5e9" },
    { title: "Purchase", icon: "fa fa-handshake", link: "/add-purchase", color: "#f97316" },
    { title: `Customers (${stats.custCount})`, icon: "fa fa-user-plus", link: "/customer-mgmt", color: "#10b981" },
    { title: `Dealers (${stats.dealerCount})`, icon: "fa fa-users", link: "/dealer-mgmt", color: "#8b5cf6" },
    { title: "Daily Income", icon: "fa fa-book", link: "/other-income", color: "#eab308" },
    { title: "Daily Expense", icon: "fa fa-money-bill", link: "/other-expense", color: "#ef4444" },
  ];

  const handleHover = e => {
    e.currentTarget.style.transform = "translateY(-5px)";
    e.currentTarget.style.boxShadow = "0 15px 30px rgba(0,0,0,0.15)";
  };

  const handleLeave = e => {
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.1)";
  };

  // 📊 UPDATED CHART CONFIGURATION WITH RE-CALCULATED DATA CORES
  const chartData = {
    labels: ["Trips Sale", "Purchase", "Today's Collection", "General Collection", "Pending Payment", "Dealer Pay"],
    datasets: [
      {
        label: "Business Summary",
        data: [
          stats.sale,
          stats.purchase,
          stats.todaysCollection,
          stats.pendingCollection,
          stats.pendingPayment,
          stats.expense
        ],
        backgroundColor: [
          "#3b82f6", // Sale
          "#f59e0b", // Purchase
          "#10b981", // Today's Collection (Payments1)
          "#14b8a6", // General Collection (Payments)
          "#f43f5e", // Pending Payment (Sale - Payments1)
          "#ef4444"  // Dealer Expense
        ],
        borderRadius: 10
      }
    ]
  };

  const marqueeStyle = `
@keyframes birthdayScroll {
    0% {
        transform: translateX(100%);
    }
    100% {
        transform: translateX(-100%);
    }
}
`;

  return (
    <Box sx={{ background: "#f3f4f6", minHeight: "100vh" }}>
      <Typography variant="h5" fontWeight={800} mb={1} color="#1e293b">
        Business Dashboard
      </Typography>

      <style>{marqueeStyle}</style>

      {birthdayAlerts.length > 0 && (

        <Paper
          sx={{
            mt: 1,
            mb: 2,
            bgcolor: "#fff7e6",
            border: "1px solid #ffd54f",
            overflow: "hidden",
            height: 42,
            display: "flex",
            alignItems: "center"
          }}
        >

          <Box
            sx={{
              whiteSpace: "nowrap",
              animation: "birthdayScroll 22s linear infinite"
            }}
          >

            <Typography
              sx={{
                color: "#d32f2f",
                fontWeight: "bold",
                fontSize: 16
              }}
            >

              🎂 Upcoming Staff Birthdays :

              {birthdayAlerts.map((staff, index) => (

                <span key={staff.id}>

                  &nbsp;&nbsp;

                  🎉

                  <b>{staff.name}</b>

                  &nbsp;

                  (
                  {new Date(staff.dob).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short"
                  })}
                  )

                  &nbsp;&nbsp;&nbsp;&nbsp;

                  {index !== birthdayAlerts.length - 1 && " | "}

                </span>

              ))}

            </Typography>

          </Box>

        </Paper>

      )}

      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          background: "linear-gradient(135deg, #f8fafc, #eef2ff)"
        }}
      >
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            label="From Date"
            type="date"
            size="small"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ background: "#fff", borderRadius: 2, minWidth: 150 }}
          />

          <TextField
            label="To Date"
            type="date"
            size="small"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ background: "#fff", borderRadius: 2, minWidth: 150 }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<TodayIcon />}
            onClick={() => {
              const today = new Date().toISOString().split("T")[0];
              setFromDate(today);
              setToDate(today);
            }}
          >
            Today
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<DateRangeIcon />}
            onClick={() => {
              const now = new Date();
              const firstDay = new Date(now.setDate(now.getDate() - now.getDay()));
              const lastDay = new Date();
              setFromDate(firstDay.toISOString().split("T")[0]);
              setToDate(lastDay.toISOString().split("T")[0]);
            }}
          >
            This Week
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<DateRangeIcon />}
            onClick={() => {
              const now = new Date();
              const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
              const lastDay = new Date();
              setFromDate(firstDay.toISOString().split("T")[0]);
              setToDate(lastDay.toISOString().split("T")[0]);
            }}
          >
            This Month
          </Button>
        </Box>
      </Paper>

      <Grid className="row" spacing={2} mb={2}>
        {BOXES.map((box, i) => (
          <Grid className="col-sm-2 mb-2" item xs={12} sm={6} md={3} key={i}>
            <Paper
              sx={{
                p: 2.5, borderRadius: 4, boxShadow: "0 4px 12px rgba(0,0,0,0.05)", cursor: "pointer",
                background: `linear-gradient(135deg, ${box.color}11, ${box.color}22)`,
                border: `1px solid ${box.color}33`,
                display: "flex", justifyContent: "space-between", alignItems: "center",
                transition: "0.3s ease-in-out",
              }}
              onMouseEnter={handleHover}
              onMouseLeave={handleLeave}
              onClick={() => box.link && box.link !== "#" && navigate(box.link)}
            >
              <Box>
                <Typography variant="body2" color="textSecondary" fontWeight={600}>{box.title}</Typography>
                <Typography variant="h5" fontWeight={800} mt={0.5}>₹{box.value.toLocaleString()}</Typography>
              </Box>
              <i className={box.icon} style={{ fontSize: 32, color: box.color }} />
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid className="row" spacing={2} mb={2}>
        {QUICK_BOXES.map((box, i) => (
          <Grid className="col-sm-2" item xs={6} sm={4} md={2} key={i}>
            <Paper
              sx={{
                p: 2, borderRadius: 3, textAlign: 'center', cursor: "pointer",
                bgcolor: '#fff', transition: "0.2s",
              }}
              onClick={() => box.link && navigate(box.link)}
            >
              <i className={box.icon} style={{ fontSize: 24, color: box.color, marginBottom: 8 }} />
              <Typography fontWeight={700} variant="body2" display="block">{box.title}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid className="row" spacing={2} sx={{
        p: 1,
        "& .MuiInputBase-root": { fontSize: "14px" },
        "& .MuiTableCell-root": { fontSize: "13px", padding: "6px 6px" },
        "& .MuiButton-root": { fontSize: "11px", padding: "4px 8px" }
      }} >
        <Grid className="col-sm-6 mb-2" item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={3}>
              Customer Payment Reminder
            </Typography>
            <Box sx={{ height: 250, overflowY: "auto" }}>
              {dueCustomers.length > 0 ? (
                <TableContainer>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "700" }}>Customer </TableCell>
                        <TableCell sx={{ fontWeight: "700" }}>Mobile</TableCell>
                        <TableCell sx={{ fontWeight: "700" }}>Due Amount</TableCell>
                        <TableCell sx={{ fontWeight: "700" }}>Due Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dueCustomers.map((c) => (
                        <TableRow key={c.id} sx={{ animation: "blink 1s infinite" }}>
                          <TableCell sx={{ color: "red", fontWeight: "700" }}>{c.customerName}</TableCell>
                          <TableCell sx={{ color: "red", fontWeight: "700" }}>{c.mobile}</TableCell>
                          <TableCell sx={{ color: "red", fontWeight: "700" }}>{c.reminderAmount}</TableCell>
                          <TableCell sx={{ color: "red", fontWeight: "700" }}>{formatDate(c.reminderDate)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No due customers.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid className="col-sm-6 mb-2" item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={3}>Today's Financial Breakdown</Typography>
            <Box sx={{ height: 250 }}>
              <Bar
                data={chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: { y: { grid: { display: false }, beginAtZero: true } }
                }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Weight Alerts Modal */}
      <Dialog
        open={openAlertPopup}
        onClose={() => setOpenAlertPopup(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, px: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 800, color: "#d32f2f", fontSize: '1.5rem' }}>
          Vehicle Weight Alerts
        </DialogTitle>
        <DialogContent dividers sx={{ borderBottom: 'none', py: 3 }}>
          <Stack spacing={2}>
            {weightAlerts.map((a) => (
              <Paper
                key={a.id}
                variant="outlined"
                sx={{
                  p: 2, borderRadius: 3, transition: "0.2s",
                  "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.08)" },
                  position: 'relative', overflow: 'hidden'
                }}
              >
                <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, bgcolor: 'error.main' }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#1a237e' }}>
                    {a.vehicleNo}
                  </Typography>
                  <Chip
                    label={`+${a.difference?.toFixed(0)} kg Over`}
                    size="small"
                    color="error"
                    sx={{ fontWeight: 700, borderRadius: 1.5 }}
                  />
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">CURRENT WEIGHT</Typography>
                    <Typography variant="body1" fontWeight={700}>{a.currentWeight} kg</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" display="block">BENCHMARK (AVG)</Typography>
                    <Typography variant="body1" fontWeight={500} color="text.primary">
                      {a.averageWeight ? `${a.averageWeight.toFixed(0)} kg` : '--'}
                    </Typography>
                  </Grid>
                  {a.previousWeight && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 0.5, borderStyle: 'dashed' }} />
                      <Typography variant="caption" color="text.secondary">
                        Last recorded: <strong>{a.previousWeight} kg</strong>
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
          <Button onClick={() => setOpenAlertPopup(false)} sx={{ color: 'text.secondary', fontWeight: 600 }}>
            Dismiss
          </Button>
          <Button
            variant="contained"
            disableElevation
            color="error"
            onClick={markAllAsRead}
            sx={{ borderRadius: 2, px: 4, py: 1, fontWeight: 700, textTransform: 'none' }}
          >
            Mark All as Read
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Dashboard;